import React, { useState, useEffect, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useWindowSize from "utilFunctions/CustomHooks/useWindowSize";
import { useGetApi } from "utilFunctions/CustomHooks/useGetAPI";
import {
  BASE_URL,
  GET_BATCH_DETAILS,
} from "utilFunctions/Constants/urlConstants";
import { ToastContextDispatch } from "Context/CommonContext";
import LectureTopBar from "./LectureTopBar";
import LectureHeader, { NavTab } from "./LectureHeader";
import LectureBottomNav from "./LectureBottomNav";
import LectureTabs from "./LectureTabs";

import {
  InfoCircleOutlined,
  UsergroupAddOutlined,
  BookOutlined,
  DesktopOutlined,
} from "@ant-design/icons";
import { LectureData } from "./LectureTypes";

const LectureDetails: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { screen } = useWindowSize();
  const isMobile = screen === "sm";
  const [activeTab, setActiveTab] = useState<string>(
    (location.state as { initialTab?: string })?.initialTab || "details"
  );
  const lecture: LectureData =
    (location.state as { lecture?: LectureData })?.lecture || {};
  const ToastDispatch = useContext(ToastContextDispatch);
  const [batchDetailsData, setBatchDetailsData] = useState<any>(null);

  const { makeRequest: GetBranchDetailsData } = useGetApi(
    (data: any) => {
      if (data?.data) {
        setBatchDetailsData(data.data);
      } else {
        setBatchDetailsData(null);
      }
    },
    (error: any) => {
      setBatchDetailsData(null);
      ToastDispatch({
        isOpen: true,
        type: "Error",
        message:
          error?.response?.data?.message ||
          error?.message ||
          "Unable to fetch batch details",
      });
    }
  );

  useEffect(() => {
    if (lecture?.batchId) {
      GetBranchDetailsData(
        `${BASE_URL}${GET_BATCH_DETAILS}?batchId=${lecture.batchId}`
      );
    }
  }, [lecture?.batchId]);

  const handleBack = () => {
    if (location.state?.from) {
      navigate(location.state.from, {
        state: { fromLectureDetails: true },
      });
    } else {
      navigate(-1);
    }
  };

  const navTabs: NavTab[] = [
    { id: "details", label: "Details", icon: <InfoCircleOutlined /> },
    { id: "attendance", label: "Attendance", icon: <UsergroupAddOutlined /> },
    ...(lecture?.eventType !== "PTM"
      ? [{ id: "syllabus", label: "Syllabus", icon: <BookOutlined /> }]
      : []),
    { id: "channels", label: "Channels", icon: <DesktopOutlined /> },
  ];

  return (
    <div
      style={{
        backgroundColor: "#f4f6f8",
        minHeight: "100vh",
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
        paddingLeft: isMobile ? "0px" : "20px",
      }}
    >
      <style>{`
        @media (max-width: 767px) {
          header,
          nav:not(.mobile-bottom-nav),
          .global-header,
          .top-navbar,
          [class*="Header"],
          [class*="Navbar"] {
            display: none !important;
          }
        }

        .desktop-header { display: block; }
        .mobile-header { display: none; }
        .mobile-banner { display: none; }
        .mobile-bottom-nav { display: none; }

        .main-container {
          padding: 20px 24px;
        }

        @media (max-width: 767px) {
          .desktop-header { display: none !important; }
          .mobile-header { display: flex !important; }
          .mobile-banner { display: block !important; }
          .mobile-bottom-nav { display: flex !important; }

          .main-container {
            padding: 12px 0px 80px 0px !important;
          }
        }
      `}</style>

      {/* MOBILE HEADER */}
      {isMobile && (
        <LectureTopBar
          lecture={lecture}
          activeTab={activeTab}
          onBack={handleBack}
        />
      )}

      <div className="main-container">
        {/* DESKTOP HEADER */}
        <LectureHeader
          lecture={lecture}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          navTabs={navTabs}
          onBack={handleBack}
        />

        <LectureTabs
          activeTab={activeTab}
          lecture={lecture}
          batchDetailsData={batchDetailsData}
        />
      </div>

      {/* MOBILE VIEW NAVIGATION BAR */}
      {isMobile && (
        <LectureBottomNav
          navTabs={navTabs}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
      )}
    </div>
  );
};

export default LectureDetails;
